import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSkeletonText,
  useIonViewWillLeave,
} from '@ionic/react';
import { checkmarkCircle } from 'ionicons/icons';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { MePrFoService } from '@/services/meprfo';
import RewardCounter from '@/components/RewardCounter';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import ShareWizardHeader from './ShareWizardHeader';
import { Destinations } from './navigation_logic';
import { useImpliedConfirm } from './useImpliedConfirm';
import { useRecipientConfirm } from './useRecipientConfirm';
import { ShareWizardLogic } from './logic';
import { ReshareStoryLogic } from './reshare_story_logic';
import { pxToRem } from '@/modules/config/constants';

const FinalShareConfirmStep: React.FC = () => {
  const {
    topic,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    impliedConfirm,
    setImpliedConfirm,
    isLoadingLegState,
    handleConfirmFinalShare,
    nav,
    totalConfirmedCount,
    lastReshareInfo,
  } = useShareWizardContext();

  const [isConfirming, setIsConfirming] = useState(false);
  // Captured before handleConfirmFinalShare clears sendQueue, so the status line
  // keeps showing the right number (and sole-recipient name) during the
  // animation.
  const [savedContactCount, setSavedContactCount] = useState(0);
  const [savedRecipientName, setSavedRecipientName] = useState('');
  // Whether the whole send was a lone share, captured at confirm time so the
  // button label stays correct after sendQueue is cleared on completion.
  const [savedIsLoneShare, setSavedIsLoneShare] = useState(false);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // A direct arrival here (cold load / fresh tab via the sender's leg link)
  // is already on the terminal step, so no forward navigation is needed.
  useImpliedConfirm();

  const currentShare = sendQueue[sendQueueCursor] ?? null;
  const contactCount = currentShare?.header.recipients.length ?? 0;
  const isComplete = totalConfirmedCount > 0 && sendQueue.length === 0;

  // News goal crossing (#447): this sender's sends put the run over the
  // top. The last initiation response's runImpressionCount already includes
  // this session's sends; totalConfirmedCount is the session's own tally.
  const crossedGoal =
    !!lastReshareInfo &&
    ReshareStoryLogic.crossedGoal(
      lastReshareInfo.runImpressionCount,
      lastReshareInfo.impressionGoal,
      totalConfirmedCount
    );
  const displayContactCount = isComplete ? savedContactCount : contactCount;

  // A recipient can tap the leg link to confirm receipt before the sender
  // confirms here. Poll the leg (until the send completes or the sender's own
  // implied-confirm beats them to it) so the status reflects a recipient-confirm.
  const recipientConfirmed = useRecipientConfirm(
    inProgressLegId,
    !!inProgressLegId && !isComplete && !impliedConfirm
  );

  // "final" only earns its place when earlier legs preceded this one. A lone
  // leg (the whole send is one message) needs no qualifier — just "Confirm
  // message sent". Mirrors the isLoneShare check in ShareWizardContext's
  // handleOpenFirstShare. On completion sendQueue is cleared, so fall back to
  // the value snapshotted at confirm time.
  const isLoneShare = isComplete ? savedIsLoneShare : sendQueue.length === 1;

  // For a single recipient, personalize the status with their short name. After
  // completion sendQueue is cleared, so fall back to the name saved at confirm
  // time. sentToLabel handles the 12-char fit and count fallback.
  const soleRecipientName = currentShare
    ? ShareWizardLogic.soleRecipientName(currentShare.header.recipients)
    : '';
  const displayRecipientName = isComplete
    ? savedRecipientName
    : soleRecipientName;
  const sentToLabel = ShareWizardLogic.sentToLabel(
    displayContactCount,
    displayRecipientName
  );

  // After the snowflake animation finishes (each of up to 5 flakes takes 400 ms),
  // hold the result for the standard toast duration then close automatically.
  useEffect(() => {
    if (!isComplete) return;
    const flakeCount = Math.min(totalConfirmedCount, 5);
    const timer = setTimeout(nav.exit, flakeCount * 400 + 5000);
    return () => clearTimeout(timer);
  }, [isComplete, totalConfirmedCount, nav.exit]);

  // On a cold load (fresh tab / killed webview) persisted state hasn't restored
  // yet, so sendQueue is empty and the status line would briefly read "Sent to 0
  // people". Show a skeleton until hydration completes. Placed after all hooks so
  // hook order stays stable.
  if (isLoadingLegState) {
    return (
      <IonPage>
        <ShareWizardHeader topicTitle="" progress={0} />
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px 20px',
              gap: '16px',
            }}
          >
            <IonSkeletonText
              animated
              style={{ height: '24px', width: '60%', borderRadius: '6px' }}
            />
            <IonSkeletonText
              animated
              style={{ height: '48px', borderRadius: '8px', marginTop: '8px' }}
            />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const handleConfirm = async () => {
    setSavedContactCount(contactCount);
    setSavedRecipientName(soleRecipientName);
    setSavedIsLoneShare(isLoneShare);
    setIsConfirming(true);
    try {
      await handleConfirmFinalShare();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleBackToShareConfirm = () => {
    nav.goTo(Destinations.send, 'back');
  };

  return (
    <IonPage>
      <ShareWizardHeader
        topicTitle={topic?.title ?? ''}
        title="All Good?"
        progress={nav.progress}
        onBack={handleBackToShareConfirm}
      />
      <IonContent style={{ '--background': 'var(--app-page-background)' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // Bottom clearance so the content clears the fixed FooterDock.
            padding: '16px 20px 120px',
            gap: '20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: pxToRem(15),
                color:
                  impliedConfirm || isComplete || recipientConfirmed
                    ? 'var(--ion-color-success)'
                    : '#ff9800',
                fontWeight: 600,
              }}
            >
              {sentToLabel} —{' '}
              {recipientConfirmed && !isComplete
                ? 'recipient-confirmed ✓'
                : impliedConfirm || isComplete
                  ? 'confirmed ✓'
                  : 'unconfirmed'}
            </p>
            <IonButton
              size="small"
              fill="outline"
              style={{
                visibility:
                  isComplete || (recipientConfirmed && contactCount === 1)
                    ? 'hidden'
                    : undefined,
              }}
              onClick={async () => {
                if (impliedConfirm && inProgressLegId) {
                  await MePrFoService.markLegAsInProgress(inProgressLegId);
                  setImpliedConfirm(false);
                }
                handleBackToShareConfirm();
              }}
              disabled={isConfirming || isComplete}
            >
              Retry
            </IonButton>
          </div>
          {isComplete && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                paddingTop: '8px',
              }}
            >
              <RewardCounter count={totalConfirmedCount} />
              {crossedGoal && lastReshareInfo ? (
                <p
                  data-testid="news-goal-celebration"
                  style={{
                    margin: 0,
                    fontSize: pxToRem(17),
                    textAlign: 'center',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  🏁 YOU got this run over the line —{' '}
                  {ReshareStoryLogic.peoplePhrase(
                    lastReshareInfo.impressionGoal ?? 0
                  )}{' '}
                  reached!
                </p>
              ) : (
                <p
                  style={{
                    margin: 0,
                    fontSize: pxToRem(17),
                    textAlign: 'center',
                    color: '#444',
                  }}
                >
                  Messages sent!
                </p>
              )}
            </div>
          )}
        </div>
      </IonContent>
      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            color="primary"
            onClick={handleConfirm}
            disabled={isConfirming || !inProgressLegId || isComplete}
            style={{ margin: 0 }}
          >
            <IonIcon slot="start" icon={checkmarkCircle} />
            {isConfirming
              ? 'Recording…'
              : impliedConfirm || recipientConfirmed
                ? 'Done'
                : isLoneShare
                  ? 'Confirm message sent'
                  : 'Confirm final message sent'}
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default FinalShareConfirmStep;
